import classNames from 'classnames';
import { FC, useState } from 'react';

const AccreditationCriteria: FC<{
  showAtComponent?: 'ONBOARDING' | 'ACCREDITATION';
  className?: string;
}> = ({ showAtComponent = 'ONBOARDING', className }) => {
  const [selectedEntityType, setSelectedEntityType] = useState<string | null>(null);

  const entityTypes = [
    {
      value: 'individual',
      label: 'Individual',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      value: 'body-corporate',
      label: 'Body Corporate',
      subLabel: '(LLP/Company/Foreign Corporation)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      value: 'trust',
      label: 'Trust (non-family)',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
    {
      value: 'llp',
      label: 'Partnership Firm',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path
            fillRule="evenodd"
            d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
            clipRule="evenodd"
          />
        </svg>
      ),
    },
  ];

  const renderIndividualCriteria = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Criterion 1: Annual Income */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 203.86 300">
              <path
                id="Path_26915"
                data-name="Path 26915"
                d="M135.76,28.35H185L203.86,0h-185L0,28.35H32.1c32.5,0,62.5,2.5,74.51,29.7H19L.15,86.39h110c0,20.4-16.9,51.65-72.8,51.65h-27v26.5L118.74,300H167L54.85,160c46.15-2.5,89.46-28.31,95.7-73.56H185L203.86,58.1h-53.8a62.85,62.85,0,0,0-14.3-29.75Z"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Annual Income</h4>
        </div>
        <p className="text-sm text-gray-600 mb-1">Annual income of at least</p>
        <p className="text-lg font-semibold text-gray-900">INR 2 Crores</p>
      </div>

      {/* Criterion 2: Net Worth */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Net Worth</h4>
        </div>
        <p className="text-sm text-gray-600 mb-1">Net worth of at least</p>
        <p className="text-lg font-semibold text-gray-900 mb-2">INR 7.5 Crores</p>
        <div className="text-xs text-gray-500 bg-white p-2 rounded border-l-2 border-indigo-400">
          <p className="font-medium">With minimum financial assets:</p>
          <p className="font-semibold">INR 3.75 Crores</p>
        </div>
      </div>

      {/* Criterion 3: Combined Income + Net Worth */}
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M3 3a1 1 0 000 2v8a2 2 0 002 2h2.586l-1.293 1.293a1 1 0 101.414 1.414L10 15.414l2.293 2.293a1 1 0 001.414-1.414L12.414 15H15a2 2 0 002-2V5a1 1 0 100-2H3zm11.707 4.707a1 1 0 00-1.414-1.414L10 9.586 8.707 8.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Combined</h4>
        </div>
        <div className="space-y-2">
          <div>
            <p className="text-sm text-gray-600">Annual income of at least</p>
            <p className="text-base font-semibold text-gray-900">INR 1 Crore</p>
          </div>
          <div className="border-t border-gray-200 pt-2">
            <p className="text-sm text-gray-600">AND net worth of at least</p>
            <p className="text-base font-semibold text-gray-900 mb-1">INR 5 Crores</p>
            <div className="text-xs text-gray-500 bg-white p-2 rounded border-l-2 border-purple-400">
              <p className="font-medium">With minimum financial assets:</p>
              <p className="font-semibold">INR 2.5 Crores</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderBodyCorporateCriteria = () => (
    <div className="grid grid-cols-1 gap-4">
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Net Worth</h4>
        </div>
        <p className="text-sm text-gray-600 mb-1">Net worth of at least</p>
        <p className="text-lg font-semibold text-gray-900">INR 50 Crores</p>
      </div>
    </div>
  );

  const renderTrustCriteria = () => (
    <div className="grid grid-cols-1 gap-4">
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4 4a2 2 0 00-2 2v4a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v4a2 2 0 01-2 2H8a2 2 0 01-2-2v-4zm6 4a2 2 0 100-4 2 2 0 000 4z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Net Worth</h4>
        </div>
        <p className="text-sm text-gray-600 mb-1">Net worth of at least</p>
        <p className="text-lg font-semibold text-gray-900">INR 50 Crores</p>
      </div>
    </div>
  );

  const renderLLPCriteria = () => (
    <div className="grid grid-cols-1 gap-4">
      <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
        <div className="flex items-center mb-3">
          <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center mr-3">
            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h4 className="font-medium text-gray-900">Partner Requirements</h4>
        </div>
        <p className="text-sm text-gray-600 mb-2">
          Each partner must independently meet the eligibility criteria for accreditation
        </p>
        <div className="text-xs text-gray-500 bg-white p-3 rounded border-l-2 border-indigo-400">
          <p className="font-medium mb-2">Individual partner criteria:</p>
          <ul className="space-y-1 text-xs">
            <li>• Annual income of at least INR 2 Crores, OR</li>
            <li>
              • Net worth of at least INR 7.5 Crores (with minimum financial assets of INR 3.75
              Crores), OR
            </li>
            <li>
              • Combined: Annual income of at least INR 1 Crore AND net worth of at least INR 5
              Crores (with minimum financial assets of INR 2.5 Crores)
            </li>
          </ul>
        </div>
      </div>
    </div>
  );

  const getCriteriaDescription = () => {
    switch (selectedEntityType) {
      case 'individual':
        return 'To qualify for accreditation, you must meet at least one of the following criteria:';
      default:
        return '';
    }
  };

  const renderCriteria = () => {
    switch (selectedEntityType) {
      case 'individual':
        return renderIndividualCriteria();
      case 'body-corporate':
        return renderBodyCorporateCriteria();
      case 'trust':
        return renderTrustCriteria();
      case 'llp':
        return renderLLPCriteria();
      default:
        return renderIndividualCriteria();
    }
  };

  return (
    <div className={classNames('bg-white col-span-full', className)}>
      {showAtComponent === 'ACCREDITATION' && (
        <h3 className="font-bold mb-1">Accreditation Criteria</h3>
      )}

      {/* Entity Type Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-4">
          Select your entity type to view accreditation criteria
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {entityTypes.map(entity => (
            <button
              key={entity.value}
              onClick={() => setSelectedEntityType(entity.value)}
              className={`p-4 border-2 rounded-lg text-left transition-all duration-200 hover:shadow-md ${
                selectedEntityType === entity.value
                  ? 'border-indigo-600 bg-indigo-50 shadow-md'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center mr-3 ${
                    selectedEntityType === entity.value
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {entity.icon}
                </div>
                <div>
                  <h4 className="font-medium text-sm text-gray-900">{entity.label}</h4>
                  {entity.subLabel ? (
                    <p className="text-xs text-gray-500 leading-tight">{entity.subLabel}</p>
                  ) : null}
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Show criteria only when entity type is selected */}
      {selectedEntityType && (
        <>
          <p className="text-sm text-gray-600 mb-4">{getCriteriaDescription()}</p>

          {renderCriteria()}

          {selectedEntityType === 'individual' ? (
            <>
              <div className="mt-4 p-3 bg-indigo-50 border border-indigo-200 rounded-lg">
                <p className="text-sm text-indigo-800">
                  <strong>Note:</strong> You only need to meet <strong>one</strong> of the above
                  criteria to qualify for accreditation. Financial assets include liquid
                  investments, stocks, bonds, mutual funds, and other marketable securities.
                </p>
              </div>
              {/* Joint Accreditation Information */}
              <div className="mt-6 p-4 border border-blue-200 rounded-lg bg-blue-50 col-span-full">
                <div className="flex items-center mb-3">
                  <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center mr-3">
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <h4 className="font-medium text-gray-900">Joint Accreditation</h4>
                </div>
                <p className="text-sm text-gray-700 mb-3">
                  <strong>Joint accreditation is permitted.</strong> You can now opt for Joint
                  accreditation.
                </p>
                <div className="space-y-3">
                  <div className="bg-white p-3 rounded border-l-2 border-blue-400">
                    <p className="text-sm font-medium text-gray-900 mb-1">With Spouse:</p>
                    <p className="text-sm text-gray-700">
                      Eligibility is based on the combined annual income and/or net worth of the
                      applicant and their spouse.
                    </p>
                  </div>
                  <div className="bg-white p-3 rounded border-l-2 border-blue-400">
                    <p className="text-sm font-medium text-gray-900 mb-1">With Parents/Children:</p>
                    <p className="text-sm text-gray-700">
                      The individual applicant's eligibility is assessed, and a joint accreditation
                      certificate may be issued accordingly.
                    </p>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

const BenefitsSection: FC = () => {
  return (
    <div className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl border border-gray-200 p-8">
      <div className="text-center mb-8">
        <h2 className="font-bold text-gray-900 mb-3">Why Get Accredited?</h2>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Unlock exclusive investment opportunities and enjoy significant regulatory benefits that
          non-accredited investors cannot access.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="text-center group">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-100 to-green-100 rounded-2xl mb-6 group-hover:scale-105 transition-transform duration-200">
            <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 203.86 300">
              <path
                id="Path_26915"
                data-name="Path 26915"
                d="M135.76,28.35H185L203.86,0h-185L0,28.35H32.1c32.5,0,62.5,2.5,74.51,29.7H19L.15,86.39h110c0,20.4-16.9,51.65-72.8,51.65h-27v26.5L118.74,300H167L54.85,160c46.15-2.5,89.46-28.31,95.7-73.56H185L203.86,58.1h-53.8a62.85,62.85,0,0,0-14.3-29.75Z"
              />
            </svg>
          </div>
          <h3 className="font-bold text-gray-900 mb-2">No Minimum Investment Limit</h3>
          <p className="text-gray-600 leading-tight">
            Build portfolios with greater flexibility with no minimum investment thresholds
          </p>
        </div>

        <div className="text-center group">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-violet-100 to-purple-100 rounded-2xl mb-6 group-hover:scale-105 transition-transform duration-200">
            <svg
              className="w-8 h-8 text-violet-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
              />
            </svg>
          </div>
          <h3 className="font-bold text-gray-900 mb-2">Continued Access</h3>
          <p className="text-gray-600 leading-tight">
            Maintain uninterrupted participation and continue to make investments with AngelList
            India
          </p>
        </div>
      </div>
    </div>
  );
};

const ExampleTemplateComponent: FC = () => {
  return (
    <div className="bg-white col-span-full mt-2">
      <img src="/ExampleTemplate.png" alt="Example Template" className="w-full h-auto" />
    </div>
  );
};

export { AccreditationCriteria, BenefitsSection, ExampleTemplateComponent };
